import { useEffect, useState } from 'react';
import {
  Typography, Container, Box, Fade,
  IconButton, Stack, Button, Divider, Grid,
} from '@mui/material';
import { FaArrowUp } from 'react-icons/fa';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ElderlyIcon from '@mui/icons-material/Elderly';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/400.css';

const STATS = [
  { labelKey: 'hero.awards',     value: '5+'   },
  { labelKey: 'hero.clients',    value: '200+' },
  { labelKey: 'hero.placements', value: '500+' },
];

const SERVICES = [
  'kandidatBanner.profesi.art',
  'kandidatBanner.profesi.babysitter',
  'kandidatBanner.profesi.elderCare',
];

const TRUST_ITEMS = [
  'hero.trust1',
  'hero.trust2',
  'hero.trust3',
];

const SERVICE_CARDS = [
  { icon: HomeWorkIcon, titleKey: 'services.houseAssistantTitle', descKey: 'services.houseAssistantDescription', profesi: 'Asisten Rumah Tangga', color: '#3b82f6', gradFrom: 'rgba(37,99,235,0.55)', gradTo: 'rgba(29,78,216,0.35)', glow: 'rgba(37,99,235,0.4)' },
  { icon: ChildCareIcon, titleKey: 'services.babySitterTitle', descKey: 'services.babySitterDescription', profesi: 'Babysitter', color: '#fb923c', gradFrom: 'rgba(249,115,22,0.55)', gradTo: 'rgba(234,88,12,0.35)', glow: 'rgba(249,115,22,0.4)' },
  { icon: ElderlyIcon, titleKey: 'services.elderCareTitle', descKey: 'services.elderCareDescription', profesi: 'Perawat Lansia', color: '#34d399', gradFrom: 'rgba(5,150,105,0.55)', gradTo: 'rgba(4,120,87,0.35)', glow: 'rgba(5,150,105,0.4)' },
];

const Home = () => {
  const { t } = useTranslation();
  const [showBackToTop, setShowBackToTop] = useState(false);

  const misiPoints = t('home.misiPoints', { returnObjects: true }) || [];

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const openWhatsApp = () => {
    window.open(`https://wa.me/${t('footer.number')}?text=${encodeURIComponent('Halo CV Mulia Utama Mandiri, saya mengetahui kontak ini melalui website Anda dan ingin bertanya lebih lanjut mengenai layanan yang tersedia. Terima kasih.')}`, '_blank');
  };

  return (
    <Box sx={{ bgcolor: '#0f172a', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: [
          'radial-gradient(ellipse 75% 65% at 12% 20%, rgba(37,99,235,0.28) 0%, transparent 65%)',
          'radial-gradient(ellipse 60% 55% at 82% 72%, rgba(249,115,22,0.16) 0%, transparent 60%)',
          'radial-gradient(ellipse 50% 40% at 60% 0%, rgba(109,75,148,0.14) 0%, transparent 55%)',
          'radial-gradient(ellipse 40% 50% at 95% 10%, rgba(37,99,235,0.08) 0%, transparent 50%)',
          '#060d1f',
        ].join(', '),
      }}>
        {/* Dot grid pattern */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1.5px, transparent 1.5px)',
          backgroundSize: '30px 30px',
          maskImage: 'radial-gradient(ellipse 90% 85% at 50% 50%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 85% at 50% 50%, black 40%, transparent 100%)',
        }} />
        {/* Subtle left-side vignette for text readability */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(100deg, rgba(6,13,31,0.55) 0%, rgba(6,13,31,0.1) 55%, transparent 100%)',
        }} />
        {/* Top brand accent line */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4, zIndex: 2,
          background: 'linear-gradient(90deg, #2563eb 0%, #f97316 100%)',
        }} />

        {/* Two-column hero layout */}
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative', zIndex: 2, flex: 1,
            display: 'flex', alignItems: 'center',
            pt: { xs: 14, md: 18 }, pb: { xs: 4, md: 8 },
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ lg: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 6, lg: 6 }}
            sx={{ width: '100%' }}
          >

            {/* ── LEFT: main copy ── */}
            <Box sx={{ maxWidth: { lg: '55%' } }}>

              {/* Company name */}
              <Typography sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 900,
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem', lg: '3.8rem' },
                lineHeight: 1.0,
                letterSpacing: '-0.04em',
                color: '#fff',
                mb: 1.5,
              }}>
                {t('home.title').split(' ').slice(0, 3).join(' ')}
                <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Box component="span" sx={{
                  background: 'linear-gradient(90deg, #f97316, #fb923c)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {t('home.title').split(' ').slice(3).join(' ')}
                </Box>
              </Typography>

              {/* Tagline */}
              <Typography sx={{
                color: 'rgba(255,255,255,0.62)',
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.65,
                mb: 4,
                maxWidth: 480,
                fontStyle: 'italic',
              }}>
                {t('home.subtitle')}
              </Typography>

              {/* Service pills */}
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 5 }}>
                {SERVICES.map(key => (
                  <Box key={key} sx={{
                    px: 2.25, py: 0.7, borderRadius: 99,
                    border: '1px solid rgba(255,255,255,0.18)',
                    bgcolor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(8px)',
                  }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.8rem', fontWeight: 600 }}>
                      {t(key)}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* CTA buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                <Button
                  component={Link} to="/kandidat"
                  variant="contained"
                  startIcon={<PeopleAltIcon sx={{ fontSize: 18 }} />}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    py: 1.65, px: 3.5, borderRadius: 99,
                    bgcolor: '#f97316', color: '#fff',
                    fontSize: '0.92rem', fontWeight: 700, textTransform: 'none',
                    boxShadow: '0 8px 28px -4px rgba(249,115,22,0.55)',
                    '&:hover': { bgcolor: '#ea6c10', boxShadow: '0 12px 32px -4px rgba(249,115,22,0.65)', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t('kandidatBanner.cta')}
                </Button>
                <Button
                  onClick={openWhatsApp}
                  variant="outlined"
                  startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    py: 1.65, px: 3.5, borderRadius: 99,
                    color: '#fff', borderColor: 'rgba(255,255,255,0.35)',
                    fontSize: '0.92rem', fontWeight: 700, textTransform: 'none',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t('footer.header')}
                </Button>
              </Stack>

              {/* Trust indicators */}
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {TRUST_ITEMS.map(key => (
                  <Stack key={key} direction="row" alignItems="center" spacing={1.25}
                    sx={{
                      px: 2, py: 1, borderRadius: 99,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#4ade80' }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.88rem', fontWeight: 600 }}>
                      {t(key)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* ── RIGHT: service cards ── */}
            <Box sx={{ display: { xs: 'none', lg: 'block' }, flexShrink: 0, width: 300 }}>
              <Stack spacing={2}>
                {SERVICE_CARDS.map(({ icon: Icon, titleKey, descKey, profesi, color, gradFrom, gradTo, glow }) => (
                  <Box
                    key={titleKey}
                    component={Link}
                    to="/kandidat"
                    state={{ profesi }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      px: 2.5, py: 2.25, borderRadius: '18px',
                      background: `linear-gradient(135deg, ${gradFrom} 0%, ${gradTo} 100%)`,
                      border: '1px solid rgba(255,255,255,0.1)',
                      textDecoration: 'none',
                      backdropFilter: 'blur(12px)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 16px 36px ${glow}`,
                      },
                    }}
                  >
                    <Box sx={{
                      width: 50, height: 50, borderRadius: '14px', flexShrink: 0,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon sx={{ color: '#fff', fontSize: '1.4rem' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.4, mb: 0.3 }}>
                        {t('services.title')}
                      </Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.3 }}>
                        {t(titleKey)}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', ml: 'auto', flexShrink: 0 }} />
                  </Box>
                ))}
              </Stack>
            </Box>

          </Stack>
        </Container>

        {/* Stats strip */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          background: 'rgba(6,13,31,0.80)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
        }}>
          <Container maxWidth="xl">
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />}
            >
              {STATS.map(({ labelKey, value }) => (
                <Box key={labelKey} sx={{ flex: 1, py: { xs: 2.5, md: 3 }, px: 2, textAlign: 'center' }}>
                  <Typography sx={{
                    color: '#f97316', fontWeight: 900,
                    fontSize: { xs: '1.8rem', md: '2.4rem' },
                    lineHeight: 1, letterSpacing: '-0.03em', fontFamily: 'Poppins, sans-serif',
                  }}>
                    {value}
                  </Typography>
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.45)', fontSize: { xs: '0.65rem', md: '0.72rem' },
                    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, mt: 0.5,
                  }}>
                    {t(labelKey)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* ── VISION & MISSION ── */}
      <Box sx={{ bgcolor: '#080e1e', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', top: '-20%', left: '-5%', width: '40%', height: '140%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-20%', right: '-5%', width: '40%', height: '140%',
          background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: { xs: 5, md: 7 } }}>
            <Box sx={{ width: 28, height: 2.5, bgcolor: '#2563eb', borderRadius: 1 }} />
            <Typography sx={{
              color: '#2563eb', fontWeight: 700, fontSize: '0.7rem',
              textTransform: 'uppercase', letterSpacing: 2.8,
            }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Stack>

          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="stretch">

            {/* VISION */}
            <Grid item xs={12} md={4}>
              <Box sx={{
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                background: 'linear-gradient(145deg, #1e3a8a 0%, #1d4ed8 100%)',
                p: { xs: 4, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}>
                <Box sx={{
                  position: 'absolute', inset: 0,
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                  pointerEvents: 'none',
                }} />
                <Box sx={{
                  position: 'absolute', right: -16, bottom: -16,
                  fontSize: '9rem', opacity: 0.07, color: '#fff',
                  lineHeight: 1, pointerEvents: 'none',
                }}>
                  <VisibilityIcon sx={{ fontSize: 'inherit' }} />
                </Box>

                <Box sx={{
                  width: 52, height: 52, borderRadius: '14px',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mb: 3, position: 'relative', zIndex: 1,
                }}>
                  <VisibilityIcon sx={{ color: '#fff', fontSize: '1.5rem' }} />
                </Box>

                <Typography sx={{
                  color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: '0.7rem',
                  textTransform: 'uppercase', letterSpacing: 2.5, mb: 1.5,
                  position: 'relative', zIndex: 1,
                }}>
                  {t('home.visi')}
                </Typography>
                <Box sx={{ width: 32, height: 3, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: 1, mb: 3, position: 'relative', zIndex: 1 }} />
                <Typography sx={{
                  color: 'rgba(255,255,255,0.82)', fontSize: '0.95rem',
                  lineHeight: 1.8, flexGrow: 1, position: 'relative', zIndex: 1,
                }}>
                  {t('home.visiDescription')}
                </Typography>
              </Box>
            </Grid>

            {/* MISSION */}
            <Grid item xs={12} md={8}>
              <Box sx={{
                height: '100%',
                borderRadius: '24px',
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                p: { xs: 4, md: 5 },
                display: 'flex',
                flexDirection: 'column',
              }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{
                    width: 52, height: 52, borderRadius: '14px',
                    bgcolor: 'rgba(249,115,22,0.12)',
                    border: '1.5px solid rgba(249,115,22,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <FlagIcon sx={{ color: '#f97316', fontSize: '1.5rem' }} />
                  </Box>
                  <Box>
                    <Typography sx={{
                      color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: '0.7rem',
                      textTransform: 'uppercase', letterSpacing: 2.5,
                    }}>
                      {t('home.misiLabel')}
                    </Typography>
                    <Typography sx={{
                      color: '#fff', fontWeight: 800, fontSize: '1.4rem',
                      lineHeight: 1.2, letterSpacing: '-0.02em',
                    }}>
                      {t('home.misi')}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ width: 32, height: 3, bgcolor: '#f97316', borderRadius: 1, mb: 4 }} />

                <Stack spacing={0} sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
                  {misiPoints.map((point, i) => (
                    <Stack key={i} direction="row" spacing={2.5} alignItems="flex-start"
                      sx={{
                        py: 2.25,
                        borderBottom: i < misiPoints.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                        transition: 'all 0.2s',
                        borderRadius: '10px',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)', px: 1, mx: -1 },
                      }}
                    >
                      <Box sx={{
                        width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                        bgcolor: 'rgba(249,115,22,0.1)',
                        border: '1px solid rgba(249,115,22,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography sx={{ color: '#f97316', fontWeight: 800, fontSize: '0.82rem' }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                      </Box>
                      <Typography sx={{
                        color: 'rgba(255,255,255,0.75)', fontSize: '0.93rem',
                        lineHeight: 1.75, pt: 0.5,
                      }}>
                        {point}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Box sx={{
                  mt: 3.5, p: 2.5, borderRadius: '14px',
                  background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(249,115,22,0.04) 100%)',
                  border: '1px solid rgba(249,115,22,0.15)',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{ width: 4, height: 36, bgcolor: '#f97316', borderRadius: 1, flexShrink: 0 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem', lineHeight: 1.65, fontStyle: 'italic' }}>
                    {t('home.subtitle')}
                  </Typography>
                </Box>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* Back to top */}
      <Fade in={showBackToTop}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed', bottom: '100px', right: '28px',
            bgcolor: '#0f172a', color: 'white',
            width: '56px', height: '56px', zIndex: 1400,
            boxShadow: '0 4px 16px rgba(15,23,42,0.3)',
            transition: '0.2s',
            '&:hover': { bgcolor: '#2563eb', transform: 'scale(1.08)' },
          }}
        >
          <FaArrowUp size={20} />
        </IconButton>
      </Fade>
    </Box>
  );
};

export default Home;
